import React from 'react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import BrandingWatermarkIcon from '@material-ui/icons/BrandingWatermark';
import BuildIcon from '@material-ui/icons/Build';
import StoreMallDirectoryIcon from '@material-ui/icons/StoreMallDirectory';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import PhoneInTalkIcon from '@material-ui/icons/PhoneInTalk';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import Container from 'common/Container';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';

const BLUE = '#00a8ff';
const CYAN = '#2dd4ff';
const CARD_BG = 'rgba(10, 18, 35, 0.9)';
const PAGE_BG = [
  'radial-gradient(ellipse 120% 64% at 50% -10%, rgba(0,168,255,0.24) 0%, transparent 52%)',
  'radial-gradient(ellipse 72% 58% at 8% 95%, rgba(28,75,183,0.26) 0%, transparent 54%)',
  'linear-gradient(130deg, rgba(4,11,22,1) 0%, rgba(12,30,56,0.98) 46%, rgba(18,52,92,0.95) 76%, rgba(45,212,255,0.24) 100%)',
].join(', ');

const sectionSx = {
  py: { xs: 5, md: 7 },
  borderBottom: '1px solid rgba(45,212,255,0.2)',
};

const cardSx = {
  p: 2.4,
  borderRadius: 2,
  bgcolor: CARD_BG,
  border: '1px solid rgba(45,212,255,0.35)',
  height: '100%',
};

function LandingMediaKitWMAutopartes() {
  return (
    <Box
      sx={{
        color: '#eef7ff',
        minHeight: '100vh',
        bgcolor: '#071021',
        backgroundImage: PAGE_BG,
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Container>
        <Box sx={{ ...sectionSx, pt: { xs: 7, md: 9 }, textAlign: 'center' }}>
          <Typography sx={{ color: CYAN, fontWeight: 800, letterSpacing: 3, mb: 1.5 }} variant="overline">
            Propuesta de Branding y Despegue Digital
          </Typography>
          <Typography component="h1" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.12, mb: 1.5 }}>
            WM Autopartes
          </Typography>
          <Typography sx={{ color: '#b7e9ff', fontWeight: 700, fontSize: { xs: '1.08rem', md: '1.25rem' }, mb: 2 }}>
            Rebranding estratégico para vender con confianza en Instagram, Marketplace y Mercado Libre
          </Typography>
          <Typography
            sx={{
              maxWidth: 930,
              mx: 'auto',
              color: 'rgba(238,247,255,0.9)',
              lineHeight: 1.7,
              fontSize: { xs: '1.02rem', md: '1.12rem' },
            }}
          >
            Objetivo: evolucionar la identidad de Waitherimotors hacia una marca robusta, confiable y competitiva. La propuesta integra branding,
            sistema visual y estandarización comercial para mejorar conversión y posicionamiento.
          </Typography>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.2 }}>
            <BrandingWatermarkIcon sx={{ color: BLUE }} />
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.95rem' } }}>
              1. Identidad de Marca y Naming
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={cardSx}>
                <Typography sx={{ color: CYAN, fontWeight: 800, mb: 1 }}>Naming estratégico</Typography>
                <Typography sx={{ color: 'rgba(238,247,255,0.88)', lineHeight: 1.6 }}>
                  Migración oficial a <strong>WM Autopartes</strong>: nombre directo, recordable y con autoridad para el mercado automotriz.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={cardSx}>
                <Typography sx={{ color: CYAN, fontWeight: 800, mb: 1 }}>Logo y sistema visual</Typography>
                <Typography sx={{ color: 'rgba(238,247,255,0.88)', lineHeight: 1.6 }}>
                  Logotipo principal, versiones claro/oscuro, isotipo para perfil y marca de agua, paleta industrial y tipografía corporativa.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.2 }}>
            <StoreMallDirectoryIcon sx={{ color: BLUE }} />
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.95rem' } }}>
              2. Adecuación del Ecosistema Digital
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {[
              {
                title: 'Biografía optimizada (Instagram)',
                text: 'Redacción enfocada en conversión, claridad de oferta y confianza inmediata.',
              },
              {
                title: 'Historias destacadas (5)',
                text: 'Portadas para Marcas, Envíos, Pagos, Clientes y Garantía con estilo unificado.',
              },
              {
                title: 'Linktree de ventas',
                text: 'Botones directos a WhatsApp, Mercado Libre y Facebook Marketplace.',
              },
            ].map((item) => (
              <Grid item xs={12} md={4} key={item.title}>
                <Box sx={cardSx}>
                  <Typography sx={{ color: CYAN, fontWeight: 800, mb: 1 }}>{item.title}</Typography>
                  <Typography sx={{ color: 'rgba(238,247,255,0.88)', lineHeight: 1.58 }}>{item.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.2 }}>
            <BuildIcon sx={{ color: BLUE }} />
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.95rem' } }}>
              3. Sistema Visual de Venta (Plantillas)
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={cardSx}>
                <Typography sx={{ color: CYAN, fontWeight: 800, mb: 1 }}>Plantilla de producto</Typography>
                <Typography sx={{ color: 'rgba(238,247,255,0.88)', lineHeight: 1.58 }}>
                  Marco visual para feed y marketplace que convierte fotos reales en apariencia de catálogo profesional.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={cardSx}>
                <Typography sx={{ color: CYAN, fontWeight: 800, mb: 1 }}>Marca de agua</Typography>
                <Typography sx={{ color: 'rgba(238,247,255,0.88)', lineHeight: 1.58 }}>
                  Protección de imagen para fotos propias y refuerzo de recordación de marca.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={cardSx}>
                <Typography sx={{ color: CYAN, fontWeight: 800, mb: 1 }}>Plantilla de historias</Typography>
                <Typography sx={{ color: 'rgba(238,247,255,0.88)', lineHeight: 1.58 }}>
                  Formato para ofertas del día y repuesto del día con enfoque comercial.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.2 }}>
            <AssignmentTurnedInIcon sx={{ color: BLUE }} />
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.95rem' } }}>
              4. Gestión de Lanzamiento
            </Typography>
          </Box>
          <Box sx={cardSx}>
            <Typography sx={{ color: CYAN, fontWeight: 800, mb: 1.1 }}>6 publicaciones iniciales</Typography>
            <Typography sx={{ color: 'rgba(238,247,255,0.88)', lineHeight: 1.6 }}>
              Diseño y redacción de seis piezas para presentar el rebranding, fortalecer confianza y mostrar productos estrella durante el arranque.
            </Typography>
          </Box>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.2 }}>
            <PhoneInTalkIcon sx={{ color: BLUE }} />
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.95rem' } }}>
              5. Plus de Operaciones y Ventas
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={cardSx}>
                <Typography sx={{ color: CYAN, fontWeight: 800, mb: 1 }}>Protocolo de respuesta rápida</Typography>
                <Typography sx={{ color: 'rgba(238,247,255,0.88)', lineHeight: 1.58 }}>
                  Guion profesional para cerrar ventas por WhatsApp y Messenger sin perder velocidad.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={cardSx}>
                <Typography sx={{ color: CYAN, fontWeight: 800, mb: 1 }}>Ficha de seguridad de envío</Typography>
                <Typography sx={{ color: 'rgba(238,247,255,0.88)', lineHeight: 1.58 }}>
                  Formato digital de confirmación de despacho con branding para elevar confianza del comprador.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={cardSx}>
                <Typography sx={{ color: CYAN, fontWeight: 800, mb: 1 }}>Guía de fotografía móvil</Typography>
                <Typography sx={{ color: 'rgba(238,247,255,0.88)', lineHeight: 1.58 }}>
                  Manual breve para que las fotos de repuestos se vean limpias, útiles y vendedoras.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ ...sectionSx, borderBottom: 'none', pb: { xs: 8, md: 10 } }}>
          <Grid container spacing={2.2} alignItems="stretch">
            <Grid item xs={12} md={7}>
              <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography sx={{ color: CYAN, fontWeight: 800, mb: 1 }} variant="overline">
                  Inversión Total
                </Typography>
                <Typography sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '2.8rem' }, mb: 1 }}>$220 USD</Typography>
                <Typography sx={{ color: 'rgba(238,247,255,0.88)', lineHeight: 1.62 }}>
                  Decisión estratégica para incluir estandarización de ventas, reforzar percepción de valor y diferenciarse de vendedores informales
                  que compiten solo por precio.
                </Typography>
                <Typography sx={{ color: '#b7e9ff', fontWeight: 700, mt: 1.2 }}>
                  Tiempo de entrega estimado: 3 a 6 semanas.
                </Typography>
                <PriceVatDisclaimer align="left" sx={{ mt: 2, color: 'rgba(238,247,255,0.75)' }} />
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <CameraAltIcon sx={{ color: CYAN, mb: 1 }} />
                <Typography sx={{ color: CYAN, fontWeight: 800, mb: 1.1 }}>Entregables finales sugeridos</Typography>
                <Typography sx={{ color: 'rgba(238,247,255,0.88)', lineHeight: 1.58 }}>
                  Logo en PNG transparente, PDF y archivo editable, más todo el kit listo para ejecución comercial.
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Typography sx={{ textAlign: 'center', color: '#b7e9ff', mt: 3, fontStyle: 'italic', fontWeight: 700 }}>
            "En repuestos, la confianza cierra ventas: WM Autopartes entra al mercado con imagen y proceso de nivel profesional."
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default LandingMediaKitWMAutopartes;

